import moment from "moment-timezone";
import _ from "lodash";

import { TotalTicketsByTypeResult } from "src/core/dashboard/api/widget-data.api";

import {
  DisplayRangeType,
  groupByDisplayRange
} from "../../utils";
import {
  TotalTicketsByTypeGraphValue,
  TotalTicketsByTypeGraphValueType
} from "../TotalTicketsByTypeWidget";

/**
 * Helper to group total tickets by type based on xAxisBy preference
 *
 * @param totalTicketsByType A list of flat total tickets by type data
 * @param displayRange DisplayRangeType
 * @param xAxisBy  "date" | "type"
 * @returns
 *
 * ### Example.
 *
 * **In:**
 * ```
 * const totalTicketsByType = [
 *  {
      "type": "Report a problem with this order",
      "date": "2022-01-31T00:00:00.000Z",
      "count": 1
    },
    {
      "type": "Report a problem with this order",
      "date": "2022-01-31T00:00:00.000Z",
      "count": 1
    },
    {
      "type": "Report a problem with this order",
      "date": "Thu Feb 03 2022 00:00:00",
      "count": 1
    }
 * ]
 * const displayRange = "month"
 * const xAxisBy = "date"
 * ```
 * **Out:**
 * ```
 *[
    {
      "date": "2022-01-01T00:00:00.000Z",
      "Report a problem with this order": 2
    },
    {
      "date": "2022-02-01T00:00:00.000Z",
      "Report a problem with this order": 1
    }
  ]
 * ```
 */
export const groupTotalTicketsByType = (totalTicketsByType: TotalTicketsByTypeResult, xAxisBy: "date" | "type", displayRange?: DisplayRangeType): TotalTicketsByTypeGraphValueType[] => {
  const result: TotalTicketsByTypeGraphValue[] = [];
  // Group the data based on xAxisBy value
  const groupedTypeData = xAxisBy === "date" && displayRange ? groupByDisplayRange(totalTicketsByType, displayRange) : _.groupBy(totalTicketsByType, "type");

  // `type` is an unknown string. So we need to create a template for accumulating the totals for each type below.
  const accumulativeDateTotalsTemplate = totalTicketsByType.reduce((currentTemplate, dataPoint) => {
    const accumulated = { ...currentTemplate };

    accumulated[ dataPoint.type ] = 0;

    return accumulated;
  }, {});

  // Loop grouped data to form the result, ensuring to include xAxisBy property and all relevant values accumulated
  for (const xAxisGroup in groupedTypeData) {
    if (xAxisBy === "date") {
      // grab the type values to accumulate total counts with

      // accumulative totals for xAxisBy date
      const accumulativeDateTotals = { ...accumulativeDateTotalsTemplate };

      groupedTypeData[ xAxisGroup ].forEach(row => {
        accumulativeDateTotals[ row.type ] += row.count;
      });

      const dataItem: TotalTicketsByTypeGraphValue = {
        date: moment.utc(xAxisGroup).toDate(),
        // Only include types that have values
        ..._.pickBy(accumulativeDateTotals, value => value !== 0)
      };

      // Force header to include date day if displayRange not by month
      if (displayRange !== "month") {
        dataItem.toolTipHeader = moment.utc(xAxisGroup).format("Do MMMM YYYY");
      }

      result.push(dataItem);
    } else {
      // accumulative total for xAxisBy type
      let accumulativeTotal = 0;

      groupedTypeData[ xAxisGroup ].forEach(row => {
        accumulativeTotal += row.count;
      });

      const dataItem: TotalTicketsByTypeGraphValue = {
        type: xAxisGroup,
        count: accumulativeTotal,
        toolTipHeader: _.startCase(xAxisGroup.replace(/[_-]/g, " "))
      };

      result.push(dataItem);
    }
  }

  return result as TotalTicketsByTypeGraphValueType[];
};
